/**
 * Hook that wires together the chat store, gateway RPC, and event stream
 * for a specific session (case or global).
 *
 * Usage:
 *   const { messages, streamingBlocks, isStreaming, send, abort } = useChatSession(sessionKey);
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useGatewayStore } from "@/stores/useGatewayStore";
import { useChatStore, type TurnActivity } from "@/stores/useChatStore";
import { useUIStore } from "@/stores/useUIStore";
import {
  readRuntimeBoot,
  runtimeDefinitelyRestarted,
  type RuntimeBootRead,
} from "@/api/lx-runtime-boot";
import { useEventStream } from "@/hooks/useEventStream";
import {
  RUN_RECONCILE_MIN_SILENCE_MS,
  RUN_STALL_REVEAL_MS,
  shouldOfferRunRelease,
} from "@/lib/run-stall";
import { useActivity, PROMPT_MAX_CHARS, truncateText } from "@/lib/activity/activity-context";
import { clientLog } from "@/lib/client-log";
import {
  buildChatErrorMessage,
  classifyProviderError,
  type ChatErrorMessage,
} from "@lexiun/lx-llm-errors";
import {
  normalizeChatEvent,
  type ChatAttachment,
  type ChatEventPayloadRaw,
  type ChatMessage,
  type LxChatHistoryParams,
} from "@/types/gateway";
import { normalizeMessageContent } from "@/types/content-blocks";
import {
  activityTarget,
  parseToolActivityFrame,
  toolBlockFromActivityFrame,
} from "@/lib/chat/tool-activity";
import { parseLxSessionKey } from "@/types/session";
import { lxScopeParams, mapLxHistoryRows } from "@/lib/chat/lx-history";
import { useChatUnreadStore } from "@/stores/useChatUnreadStore";
import {
  buildTrailAttribution,
  hasAttributableBlocks,
} from "@/lib/chat/trail-attribution";

/** Heuristic: does an assistant message contain ONLY the injected
 *  Lexiun context preamble (no user-visible content)?  Used to filter
 *  these out of the displayed history. The preamble lands as text-only
 *  via the legacy `before_prompt_build` path; check the first text
 *  block for the canonical sentinel. */
function looksLikeInjectedContext(text: string): boolean {
  return text.includes("=== LEXIUN LEGAL WORKSPACE CONTEXT ===");
}

// Operational-log channel — surfaces chat failures (agent-reply errors,
// chatSend RPC rejections) to the centralized `operationalLogs` Mongo
// collection so platform admins see them without needing to ask the
// user "what did the error message say?". Activity-log tracking already
// exists below for the per-user timeline; this is a parallel channel
// for the platform-admin viewer.
const log = clientLog.forSubsystem("chat");

// ── P6 item 2 — paged history knobs ─────────────────────────────────────────
/** Initial tail page. Matches the server's own DEFAULT_PAGE_LIMIT so the
 *  first paint shows the same window a full read used to end on. */
const HISTORY_PAGE_LIMIT = 100;
/** Tail-poll page — only needs to cover what can land between two ticks. */
const TAIL_POLL_LIMIT = 20;
/**
 * Tail-poll period. ⚖ D-8 delivery latency is a ~5–65 s RANGE (measured:
 * 58.3/34.1/31.6/20.7 s) — design to the CEILING: 30 s means a bus-origin
 * turn the WS missed is on screen at most ~half the ceiling after it lands.
 * `BUS_CONCIERGE_POLL_MS` does NOT move that ceiling; do not "tune" this
 * against it.
 */
const TAIL_POLL_MS = 30_000;
/** Server-side clamp on `limit` (MAX_PAGE_LIMIT in conversation-store.ts) —
 *  asking for more returns 500 + hasMore, never an error. Mirrored here so
 *  the reconnect-window request below stays inside it knowingly. */
const MAX_HISTORY_PAGE_LIMIT = 500;

/**
 * DEV-only record of `chat` frames this hook received and then DISCARDED.
 *
 * There are exactly two such places — a `sessionKey` that addresses another
 * session, and a `state` outside the handled vocabulary — and both are
 * legitimate "ignore it" outcomes, which is precisely why they were invisible.
 * A frame could be emitted correctly, arrive correctly, and render nothing,
 * with no signal at any layer that it had been thrown away.
 *
 * Deliberately a TALLY on `window` rather than a console warning: on a busy
 * socket the sessionKey case is constant and normal, so warning per frame
 * would be noise nobody reads, and noise nobody reads is the same as silence.
 * Query it from devtools with `__chatDropped`.
 *
 * Tree-shaken out of production builds — every call site is behind
 * `import.meta.env.DEV`.
 */
function recordDroppedChatFrame(
  reason: "sessionKey-mismatch" | "unhandled-state",
  state: string | undefined,
  sessionKey: string | undefined,
): void {
  const w = window as unknown as {
    __chatDropped?: Record<string, { n: number; lastState?: string; lastSessionKey?: string }>;
  };
  w.__chatDropped ??= {};
  const slot = (w.__chatDropped[reason] ??= { n: 0 });
  slot.n += 1;
  slot.lastState = state;
  slot.lastSessionKey = sessionKey;
}

interface UseChatSessionResult {
  messages: ChatMessage[];
  /** Mid-flight assistant content as typed blocks (replaces the
   *  legacy `streamingText: string`). Empty when no run is in flight. */
  streamingBlocks: import("@/types/content-blocks").ContentBlock[];
  isStreaming: boolean;
  /** Live work state of the in-flight turn — what the in-flow status line says
   *  and the frozen "Trabalhou por…" duration. `workedMs === null` means the
   *  answer-start was never observed, and the renderer shows NO duration for
   *  it (never zero, never a substitute). */
  activity: TurnActivity;
  isLoading: boolean;
  /** `true` once the initial `chat.history` load has settled (any
   *  outcome). Surfaces use it to defer their empty-state decision so
   *  a "no messages" hero doesn't flash before history hydrates. */
  historyLoaded: boolean;
  /** Classified `ChatErrorMessage` envelope (common case) or a raw string for
   *  unclassified failures; the render layer dispatches on shape. */
  error: string | ChatErrorMessage | null;
  /** ⚖ WEDGE row — the run has produced NOTHING past the reveal threshold, so
   *  the UI should OFFER `releaseStalledRun`. Never a claim that the run died:
   *  a slow turn and a hung runtime are indistinguishable from the browser. */
  isStalled: boolean;
  /** Clear this browser's lock. LOCAL ONLY — makes no claim about the server,
   *  which is the process that is not answering. */
  releaseStalledRun: () => void;
  /** Send a chat message. `attachments` rides along — e.g. a
   *  petition-selection payload the server's before_prompt_build
   *  hook reshapes into the `<selected_range>` preamble.
   *  `selection` is the viewer text-selection the lawyer had active
   *  when they hit send — gets stamped onto the optimistic
   *  `ChatMessage.selection` so the bubble's echo sub-line renders
   *  immediately (and is also captured server-side as
   *  `LxConversationMessage.context` for the durable reload echo).
   *  See handoff `lexide-retire-sent-selection-store-handoff-2026-06-29.md`. */
  send: (
    message: string,
    attachments?: ChatAttachment[],
    selection?: { source: string; text: string },
    mentions?: { path: string; name: string }[],
    voice?: { audioUrl?: string },
  ) => void;
  abort: () => void;
  /** Re-run the most recent turn by re-sending the last user message
   *  verbatim. This is a *stopgap*, NOT a true regenerate: the gateway has
   *  no "re-run this run" RPC, so retry simply submits the prior prompt
   *  again as a fresh turn (appending a new user + assistant pair) rather
   *  than replacing the previous assistant reply in place. No-op when the
   *  session has no user message yet. */
  retryLast: () => void;
  /** THIRD STATE of the initial history read (P6 item 2): the read FAILED.
   *  Surfaces MUST render this as "could not load", never as an empty
   *  thread — empty WITHOUT it means no history, empty WITH it means we
   *  could not look. */
  historyReadError: string | null;
  /** Older messages exist before the loaded window (MEASURED server-side).
   *  Drives the back-scroll affordance. */
  hasMoreHistory: boolean;
  /** A back-scroll page fetch is in flight. */
  loadingOlder: boolean;
  /** A back-scroll page fetch failed (e.g. a rejected cursor). Rendered as
   *  an inline row; never auto-retried bare. */
  olderError: string | null;
  /** Fetch the block BEFORE the oldest loaded message via the paged cursor.
   *  No-ops while loading, when nothing older exists, or without a cursor. */
  loadOlder: () => void;
}

export function useChatSession(sessionKey: string): UseChatSessionResult {
  const rpc = useGatewayStore((s) => s.rpc);
  const connectionState = useGatewayStore((s) => s.connectionState);
  const session = useChatStore((s) => s.getSession(sessionKey));
  const store = useChatStore;
  const activity = useActivity();
  // Stable ref so the event-stream callback (memoised on [sessionKey, store])
  // can read the current activity context without forcing a re-subscription
  // every time the provider value changes.
  const activityRef = useRef(activity);
  activityRef.current = activity;
  const historyLoadedRef = useRef<string | null>(null);
  // Stable ref for the same reason as `activityRef` — the chat event callback
  // must not re-subscribe when the rpc identity changes.
  const rpcRef = useRef(rpc);
  rpcRef.current = rpc;

  // ── ⚖ A-11 turn-end provenance hydration ──────────────────────────────────
  // The LIVE wire cannot carry a provider (D5 strips results; `meta` is built
  // from args at call start — a11-provider-badge-emit-miniplan.md §5.1), so
  // when a live turn ends we re-read the durable row and merge its trail's
  // provider/path onto the finalized message via (name, ordinal) matching
  // (`lib/chat/trail-attribution.ts`). Called ONLY when a final genuinely
  // ended the active run — receipts and compaction segment boundaries never
  // reach it.
  const hydrateAttribution = useCallback(
    (runId: string) => {
      const lxCtx = parseLxSessionKey(sessionKey);
      if (!lxCtx || (lxCtx.type !== "case" && lxCtx.type !== "global")) return;
      const message = store
        .getState()
        .getSession(sessionKey)
        .messages.find((m) => m.runId === runId && m.role === "assistant");
      // No web call in the turn ⇒ nothing a badge could attach to — skip the
      // thread-read entirely rather than fetching to learn nothing.
      if (!message || !hasAttributableBlocks(message.content)) return;
      const params: LxChatHistoryParams = lxScopeParams(lxCtx);
      // `true` = SETTLED (row read; whatever it said is the answer).
      // `false` = row not on disk yet — a retry may still see it.
      const attempt = (): Promise<boolean> => {
        const rpcNow = rpcRef.current;
        if (!rpcNow) return Promise.resolve(true);
        return rpcNow.lxChatHistory(params).then((result) => {
          const row = result?.messages?.find(
            (m) => m.runId === runId && m.role === "assistant",
          );
          if (!row) return false;
          // A v:2 row is a MEASURED "the writer could not know" (pre-A-11
          // emitter) — retrying cannot change it; a v>=3 row without provider
          // anywhere merges to {} and the store leaves the message untouched.
          if ((row.v ?? 1) < 3 || !row.toolTrail?.length) return true;
          store
            .getState()
            .hydrateTurnAttribution(
              sessionKey,
              runId,
              buildTrailAttribution(message.content, row.toolTrail),
            );
          return true;
        });
      };
      attempt()
        .then((settled) => {
          if (!settled) {
            // The row is persisted at agent_end and can lose the race with the
            // final frame — ONE delayed retry, then give up SILENTLY. The badge
            // stays absent until reload, which is honest; the ceiling is a
            // give-up signal, never a completion signal (no state is invented
            // when it trips).
            setTimeout(() => {
              attempt().catch(() => {});
            }, 2_000);
          }
        })
        .catch(() => {});
    },
    [sessionKey, store],
  );
  const hydrateAttributionRef = useRef(hydrateAttribution);
  hydrateAttributionRef.current = hydrateAttribution;

  // Subscribe to "chat" events, normalize payload to ContentBlock[],
  // filter by sessionKey. The chat-content-model redesign promotes
  // every block type (text/thinking/tool_use/tool_result/image/...)
  // through to the store + render layer — no more text-only flattening.
  useEventStream("chat", useCallback((payload: unknown) => {
    // Any frame for this session is proof of life — stamp BEFORE any filtering
    // so an unrecognised or ignored frame still counts as activity.
    lastEventAtRef.current = Date.now();
    const raw = payload as ChatEventPayloadRaw;
    if (raw.sessionKey !== sessionKey) {
      // A frame addressed elsewhere is NORMAL (one socket, many sessions), so
      // this is not an error — but it is also the first of two places a line
      // can vanish without trace, so DEV records it. See the `default` branch
      // below for the second. Both exist because 2026-08-03 cost two lanes
      // hours on exactly this: a receipt that was emitted, arrived, and
      // rendered nothing, with no signal anywhere that it had been discarded.
      if (import.meta.env.DEV) {
        recordDroppedChatFrame("sessionKey-mismatch", raw.state, raw.sessionKey);
      }
      return;
    }

    const evt = normalizeChatEvent(raw);
    const state = store.getState();
    // B6→F3: a deterministic FALLBACK line stands in for a reaction turn that
    // never delivered. Clear the progress state keyed to that run BEFORE the
    // line lands, so the spinner does not outlive the sentence explaining why
    // there is no reaction. Cheap and idempotent — `abandonServerTurn` only
    // clears when that run is still the active one.
    if (raw.replacesRunId) {
      state.abandonServerTurn(sessionKey, raw.replacesRunId);
    }
    switch (evt.state) {
      case "delta":
        // Today's wire sends cumulative snapshots — each delta carries
        // the full block array for the in-flight run. setStreamingBlocks
        // REPLACES the buffer with the snapshot.
        if (evt.blocks.length > 0) {
          state.setStreamingBlocks(sessionKey, evt.runId, evt.blocks);
        }
        break;
      case "final": {
        const wasActive = state.getSession(sessionKey).activeRunId === evt.runId;
        state.finalizeRun(sessionKey, evt.runId, evt.blocks, {
          model: evt.model,
          provider: evt.provider,
        });
        // ⚖ A-11 — hydrate provenance only when THIS final genuinely ended the
        // active run. An unsolicited final (receipt) and a compaction segment
        // boundary both leave `activeRunId` untouched and must not trigger a
        // thread-read.
        const endedNow =
          wasActive && store.getState().getSession(sessionKey).activeRunId !== evt.runId;
        if (endedNow) hydrateAttributionRef.current(evt.runId);
        break;
      }
      case "error": {
        const errMsg = evt.error ?? "Unknown error";
        // Classify the raw upstream string CLIENT-SIDE — the chat WS frame is
        // emitted by frozen openclaw core and can't be rewritten server-side,
        // but LexIDE already holds the string. The structured envelope drives
        // the localized, actionable error card AND enriches the logs below with
        // a machine-readable kind. See ai-docs/lexruntime-error-handling-plan.md
        // Pillar B.
        const classification = classifyProviderError(errMsg);
        const envelope = buildChatErrorMessage(classification, { traceId: evt.runId });
        state.setError(sessionKey, envelope);
        // L7 — capture chat-error frames coming over the WS event
        // stream (distinct from chatSend-promise rejections, which
        // are captured in send()'s .catch()).
        activityRef.current?.track("error", "chat.error", {
          session_key: sessionKey,
          run_id: evt.runId,
          message: truncateText(errMsg, 1_024),
          source: "event_stream",
          error_kind: classification.kind,
          error_recovery: envelope.affordance,
          matched_pattern: classification.matchedPattern,
        });
        // Platform-admin operational-log channel — see file-level commentary on
        // the `log` const. `error_kind` makes the operational-logs viewer
        // filterable by failure class (e.g. auth-revoked).
        log.error("chat.error.event", {
          session_key: sessionKey,
          run_id: evt.runId,
          message: truncateText(errMsg, 1_024),
          error_kind: classification.kind,
          error_recovery: envelope.affordance,
          matched_pattern: classification.matchedPattern,
        });
        break;
      }
      case "aborted":
        state.abortRun(sessionKey, evt.blocks);
        break;
      default:
        // An unrecognised `state` is passed through verbatim by
        // `normalizeChatEvent`, so before this branch existed the frame fell
        // out of the switch and vanished. The wire's state vocabulary is the
        // RUNTIME's to widen, not this file's — so the right behaviour is
        // still to ignore it, but never SILENTLY.
        if (import.meta.env.DEV) {
          recordDroppedChatFrame("unhandled-state", evt.state, raw.sessionKey);
        }
        break;
    }
  }, [sessionKey, store]));

  // ── session.tool → live activity bridge (L1) ──────────────────────────────
  // The "chat" wire is TEXT-ONLY (frozen core emits [{type:"text"}] in delta
  // AND final payloads) so tool_use blocks can never arrive above. The tool
  // lifecycle IS delivered — on the session.tool EventFrames the Phase-5
  // sessions.subscribe seam already turns on.
  //
  // v1 bridged exactly TWO names (lx_todo/update_plan → TodoCard) and dropped
  // every other frame on the floor. L1 widens that to the D1 curated set
  // (`lib/chat/tool-activity.ts`), which owns the set, the friendly labels and
  // the parse together so they cannot drift. Mission tools stay excluded there
  // by an explicit denylist, not by omission.
  //
  // `phase:"result"` records a MEASURED outcome (`isError` survives the
  // server-side payload strip), so completion marks never guess.
  // See ai-docs/lexide-concierge-live-activity-l0-l1-miniplan.md.
  useEventStream("session.tool", useCallback((payload: unknown) => {
    const frame = parseToolActivityFrame(payload, sessionKey);
    if (!frame) return;
    const state = store.getState();
    if (frame.phase === "start") {
      state.upsertLiveToolBlock(sessionKey, toolBlockFromActivityFrame(frame));
      const target = activityTarget(frame.name, frame.args);
      state.noteToolStart(sessionKey, { name: frame.name, ...(target ? { target } : {}) });
      return;
    }
    if (frame.phase === "result") {
      state.noteToolResult(sessionKey, frame.callId, frame.isError);
    }
  }, [sessionKey, store]));

  // Reset history loaded flag when session changes or gateway reconnects
  const prevConnectionRef = useRef(connectionState);
  const justReconnectedRef = useRef(false);
  if (connectionState === "connected" && prevConnectionRef.current !== "connected") {
    // Gateway just reconnected — reload history to catch messages from other clients
    historyLoadedRef.current = null;
    justReconnectedRef.current = true;
  }
  prevConnectionRef.current = connectionState;

  const activeRunId = session.activeRunId;

  // ── WEDGE row: reveal a MANUAL release when a run goes silent ─────────────
  //
  // `bootId` below proves a RESTART; this covers the case it cannot see — the
  // runtime alive but wedged, emitting nothing, with no reconnect to trigger
  // anything and a `chat.abort` RPC that goes to the stuck process itself.
  //
  // ⚠ REVEALS a control, never acts. See `lib/run-stall.ts` for why an
  // automatic abort would be worse than the lock it replaces.
  const lastEventAtRef = useRef<number | null>(null);
  const [stalled, setStalled] = useState(false);

  useEffect(() => {
    if (!activeRunId) {
      // A new run starts the clock; no run means nothing to offer.
      lastEventAtRef.current = null;
      setStalled(false);
      return;
    }
    // Starting the clock HERE (not at the first delta) is deliberate: a run that
    // never produces a single event is precisely the wedge we are catching, and
    // waiting for a first event would mean never offering the escape for it.
    if (lastEventAtRef.current === null) lastEventAtRef.current = Date.now();
    setStalled(false);
    const id = setInterval(() => {
      setStalled(
        shouldOfferRunRelease({
          activeRunId,
          lastEventAt: lastEventAtRef.current,
          now: Date.now(),
        }),
      );
      // Polled rather than a single timer so the deadline re-evaluates against
      // the LATEST event; a timer armed once would fire on a run that has been
      // streaming happily the whole time.
    }, 5_000);
    return () => clearInterval(id);
  }, [activeRunId, sessionKey]);

  /** Release this browser's lock. Local ONLY — it makes no claim about the
   *  server, which is the process that is not answering. */
  const releaseStalledRun = useCallback(() => {
    const live = store.getState().getSession(sessionKey).activeRunId;
    if (!live) return;
    log.warn("chat.run.released_by_user_after_silence", {
      session_key: sessionKey,
      run_id: live,
      silent_for_ms: lastEventAtRef.current ? Date.now() - lastEventAtRef.current : null,
      threshold_ms: RUN_STALL_REVEAL_MS,
    });
    store.getState().abortRun(sessionKey);
    setStalled(false);
  }, [store, sessionKey]);

  // ── Runtime-restart detection: release a run whose owner is GONE ──────────
  //
  // `isStreaming` is `activeRunId !== null`, and the ONLY things that clear it
  // are wire events. When the runtime dies mid-turn no event ever arrives, so
  // the promptbox stays locked until a full page reload — the permalock
  // `go-live` reported from a prod incident.
  //
  // `bootId` is the primitive that makes this exact instead of a guess: if it
  // CHANGED across a reconnect, the process that owned the run no longer
  // exists, so the run is definitively dead. Anything else — unchanged, absent
  // (pre-`c8a89cfe7` runtime), unreadable — concludes nothing and we leave the
  // run alone. See `lx-runtime-boot.ts` for why those three are distinct.
  //
  // ⚠ Deliberately NOT a wall-clock timeout. A legitimately long turn would
  // abort itself, trading a stuck promptbox for a lying one — and a lie that
  // discards the lawyer's work is worse than a lock they can see.
  //
  // ⚠ This does NOT cover a runtime that is alive but WEDGED (hung event loop:
  // no sweep, and no reconnect to trigger this either — measured, the
  // 2026-08-08 `sgb4p9j6` hang ran ~18 min with the process never dying). That
  // row needs a user-facing escape affordance; it is not fixable from here.
  const lastBootRef = useRef<RuntimeBootRead | null>(null);
  useEffect(() => {
    if (connectionState !== "connected") return;
    const reconnected = justReconnectedRef.current;
    justReconnectedRef.current = false;
    let cancelled = false;
    const ac = new AbortController();
    void (async () => {
      const read = await readRuntimeBoot(ac.signal);
      if (cancelled) return;
      const previous = lastBootRef.current;
      // Only record a CONCLUSIVE read as the new baseline. Letting an
      // `unreadable` overwrite a good `present` would erase the id we need to
      // compare against, and the next reconnect could then never detect the
      // restart it exists to catch.
      if (read.state === "present") lastBootRef.current = read;
      else if (!previous) lastBootRef.current = read;

      if (!reconnected) return;
      if (!runtimeDefinitelyRestarted(previous, read)) return;
      // Re-read from the store rather than closing over `activeRunId`: the
      // await above is a suspension point, and the run may have finalized
      // normally while the version request was in flight.
      const live = store.getState().getSession(sessionKey).activeRunId;
      if (!live) return;
      log.warn("chat.run.released_after_runtime_restart", {
        session_key: sessionKey,
        run_id: live,
      });
      store.getState().abortRun(sessionKey);
    })();
    return () => {
      cancelled = true;
      ac.abort();
    };
    // `activeRunId` participates so a run that starts AFTER the reconnect
    // still gets a baseline recorded for the next one.
  }, [connectionState, activeRunId, sessionKey, store]);

  // Load chat history on mount / session change / reconnection
  useEffect(() => {
    if (!rpc || connectionState !== "connected") return;
    if (historyLoadedRef.current === sessionKey) return;

    historyLoadedRef.current = sessionKey;

    // Lexiun conversation-history store (reload-survival): the case/global MAIN
    // chat replays from `lx.chat_history` (un-pruned) instead of core
    // `chat.history`, which drops text-only assistant replies by design. We pass
    // ids/scope (NOT a raw session key) so the server re-derives the key and the
    // case-bound JWT validates it. Sub-scope surfaces keep the core path below.
    // See ai-docs/lexiun-conversation-history-plan.md.
    const lxCtx = parseLxSessionKey(sessionKey);
    if (lxCtx && (lxCtx.type === "case" || lxCtx.type === "global")) {
      const params = lxScopeParams(lxCtx);
      // P6 item 2 — the PAGED call (`3168dd1a8`). An explicit `limit` selects
      // the bounded read path: the server reads backward from the tail and
      // stops once the page is assembled, returns MEASURED `hasMore` + a
      // `cursor` for back-scroll, and can report `readError` — the third
      // state the full read structurally cannot express.
      //
      // Reconnect keeps the reader's WINDOW: this load also runs on gateway
      // reconnects, and `setHistory`'s merge treats in-store rows older than
      // the fetched page as stale locals and drops them — so a fixed limit
      // would silently shrink a back-scrolled thread to the newest page on
      // every reconnect. Ask for at least what is already loaded (clamped to
      // the server max; beyond it, hasMore lets the reader re-scroll).
      const loadedCount = store.getState().getSession(sessionKey).messages.length;
      const limit = Math.min(
        Math.max(HISTORY_PAGE_LIMIT, loadedCount),
        MAX_HISTORY_PAGE_LIMIT,
      );
      rpc
        .lxChatHistory({ ...params, limit })
        .then((result) => {
          store.getState().markHistoryLoaded(sessionKey);
          if (result?.readError) {
            // THIRD STATE — "we could not look". Never rendered as an empty
            // thread: empty WITHOUT readError means no history, empty WITH it
            // means the read failed, and conflating them shows a lawyer an
            // empty case that is not empty.
            store.getState().setHistoryReadError(sessionKey, result.readError);
            return;
          }
          store.getState().setHistoryReadError(sessionKey, null);
          store.getState().setHistoryPage(sessionKey, {
            ...(result?.cursor ? { cursor: result.cursor } : {}),
            // Absent on a pre-paging runtime → reads as false (no back-scroll
            // affordance), never as a guess from page fullness.
            hasMore: result?.hasMore === true,
          });
          if (result?.messages?.length) {
            // Row → ChatMessage mapping shared with back-scroll pages and the
            // tail poll (`lib/chat/lx-history.ts`) so the three cannot drift.
            store
              .getState()
              .setHistory(sessionKey, mapLxHistoryRows(result.messages, params));
          }
        })
        .catch((err: unknown) => {
          // A transport-level failure is ALSO "we could not look" — before the
          // third state existed this settled as a false-empty thread.
          store.getState().markHistoryLoaded(sessionKey);
          store
            .getState()
            .setHistoryReadError(
              sessionKey,
              err instanceof Error ? err.message : "history load failed",
            );
        });
      return;
    }

    rpc.chatHistory({ sessionKey }).then((result) => {
      // Settle the surface's empty-vs-populated gate regardless of
      // whether history came back non-empty (below) or empty.
      store.getState().markHistoryLoaded(sessionKey);
      if (result?.messages?.length) {
        // Rich-content-model normalization (post-redesign): the wire's
        // assistant `content` is a block array carrying text + thinking
        // + tool_use + tool_result + image. `normalizeMessageContent`
        // reconciles the per-provider variants into our canonical
        // ContentBlock[]. NO destructive text-only filter — every block
        // type we recognize is preserved + flows through to render.
        // See ai-docs/lexiun-chat-content-model-plan.md §2.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const messages: ChatMessage[] = (result.messages as any[])
          .map((m: Record<string, unknown>) => {
            const blocks = normalizeMessageContent(m.content);
            const ts = m.timestamp;
            const timestamp = typeof ts === "number"
              ? new Date(ts).toISOString()
              : typeof ts === "string" ? ts : new Date().toISOString();

            return {
              role: (m.role as ChatMessage["role"]) ?? "assistant",
              content: blocks,
              timestamp,
              runId: m.runId as string | undefined,
              model: m.model as string | undefined,
              provider: m.provider as string | undefined,
            };
          })
          .filter((m) => {
            // Skip system messages
            if (m.role === "system") return false;
            // Skip messages containing ONLY the injected Lexiun context
            // preamble (legacy `before_prompt_build` output). Detect via
            // the first text block — the preamble lands as plain text
            // so this catches it without false-positives on real chat.
            const firstText = m.content.find((b) => b.type === "text");
            if (firstText && firstText.type === "text" && looksLikeInjectedContext(firstText.text)) return false;
            // Skip empty assistant messages (error responses with no
            // content blocks at all). Note: a message with ONLY a
            // tool_use block is NOT empty — we keep it.
            if (m.role === "assistant" && m.content.length === 0) return false;
            return true;
          });
        store.getState().setHistory(sessionKey, messages);
      }
    }).catch(() => {
      // History load failure is non-fatal — user can still send new
      // messages. Still settle the gate so the empty-state surface (and
      // the global-home composer) renders instead of spinning forever.
      store.getState().markHistoryLoaded(sessionKey);
    });
  }, [rpc, connectionState, sessionKey, store]);

  const send = useCallback(
    (
      message: string,
      attachments?: ChatAttachment[],
      // 3rd positional arg — the viewer text-selection the lawyer had
      // active when they hit send. Threaded directly to addUserMessage
      // so the optimistic bubble's echo sub-line renders immediately
      // (replaces the in-memory `useSentSelectionStore` bridge per
      // handoff `lexide-retire-sent-selection-store-handoff-2026-06-29.md`).
      // The selection still rides the invisible
      // `runtime.system.enqueue_event` directive separately for the
      // agent prompt — this arg only annotates the local-render copy.
      selection?: { source: string; text: string },
      // 4th positional arg — referenced workspace files (@-mention /
      // drag-drop). Like `selection`, this only annotates the optimistic
      // local bubble; the paths themselves ride the separate invisible
      // `runtime.system.enqueue_event` directive (source
      // `lexide-spa:referenced-files`) fired by ChatInput before this call.
      mentions?: { path: string; name: string }[],
      // 5th positional arg — voice-dictation marker (auto-submitted mic
      // recording, operator brief 2026-07-02). Annotates the optimistic
      // bubble so it renders the voice-note header + local playback; the
      // transcript itself is the plain `message`.
      voice?: { audioUrl?: string },
    ) => {
      if (!rpc || connectionState !== "connected" || !message.trim()) return;

      const idempotencyKey = crypto.randomUUID();
      const trimmed = message.trim();
      store.getState().addUserMessage(sessionKey, trimmed, idempotencyKey, selection, mentions, voice);

      // Miniplan H2 — the first-run signal. Placed AFTER the guard above so it
      // only fires on a real, connected, non-empty send: opening the app and
      // hitting Enter on an empty composer is exactly the "exits without typing
      // anything" case the operator said must still see the welcome next time.
      // Write-once inside the store, so this costs nothing after the first.
      useUIStore.getState().markFirstActivity();

      // L7 — emit prompt activity. PII scrubbing happens inside the
      // provider; we just truncate to PROMPT_MAX_CHARS here so big
      // prompts don't bloat the outbox.
      activity?.track("prompt", "chat.prompt_submit", {
        prompt: truncateText(trimmed, PROMPT_MAX_CHARS),
        prompt_len: trimmed.length,
        session_key: sessionKey,
        attachment_count: attachments?.length ?? 0,
        idempotency_key: idempotencyKey,
      });

      rpc
        .chatSend({
          sessionKey,
          message: trimmed,
          idempotencyKey,
          ...(attachments && attachments.length > 0 ? { attachments } : {}),
        })
        .catch((err) => {
          const msg = (err as Error).message;
          // A client-side gateway RPC timeout (`Request timeout: chat.send …`) is
          // NOT a provider outage — the agent is taking longer than the chat budget
          // (a long, reasoning-heavy turn may still be finishing). Surface a distinct
          // "taking a while" card instead of misclassifying it as `provider-unavailable`.
          const isClientTimeout = /^Request timeout: chat\.send/i.test(msg);
          const classification = isClientTimeout ? null : classifyProviderError(msg);
          const envelope: ChatErrorMessage = classification
            ? buildChatErrorMessage(classification, { traceId: idempotencyKey })
            : {
                kind: "chat-timeout",
                affordance: "manual-retry",
                tone: "info",
                i18nKey: "llmErrors.llm.chat-timeout",
                traceId: idempotencyKey,
              };
          const matchedPattern = classification?.matchedPattern ?? "client:chat.send-timeout";
          store.getState().setError(sessionKey, envelope);
          activity?.track("error", "chat.error", {
            session_key: sessionKey,
            idempotency_key: idempotencyKey,
            message: truncateText(msg, 1_024),
            error_kind: envelope.kind,
            error_recovery: envelope.affordance,
            matched_pattern: matchedPattern,
          });
          // Platform-admin operational-log channel — chatSend
          // RPC-promise rejections (transport-level failures,
          // distinct from WS-event chat errors).
          log.error("chat.error.rpc", {
            session_key: sessionKey,
            idempotency_key: idempotencyKey,
            message: truncateText(msg, 1_024),
            error_kind: envelope.kind,
            error_recovery: envelope.affordance,
            matched_pattern: matchedPattern,
          });
        });
    },
    [rpc, connectionState, sessionKey, store, activity],
  );

  const abort = useCallback(() => {
    if (!rpc) return;
    rpc.chatAbort({ sessionKey }).catch(() => {});
  }, [rpc, sessionKey]);

  // Retry = re-send the last user message verbatim (see interface doc).
  // We read the freshest session straight from the store so the closure
  // doesn't need `session` in its dep array (avoids re-creating the
  // callback on every streamed delta). The user message content already
  // carries any AIEditor preamble baked in at first-send, so re-submitting
  // `content` reproduces the exact wire prompt — attachments aside, which
  // the stopgap intentionally does not re-attach.
  const retryLast = useCallback(() => {
    const sess = store.getState().getSession(sessionKey);
    for (let i = sess.messages.length - 1; i >= 0; i--) {
      const m = sess.messages[i];
      if (m.role === "user") {
        // User messages carry plain-text blocks (composer only emits
        // text today; attachments travel side-channel via chat.send).
        const text = m.content
          .filter((b) => b.type === "text")
          .map((b) => (b.type === "text" ? b.text : ""))
          .join("");
        if (text) send(text);
        return;
      }
    }
  }, [store, sessionKey, send]);

  // ── P6 item 2 — back-scroll pagination ────────────────────────────────────
  // Fetch the block strictly BEFORE the oldest loaded message. The cursor is
  // EXACT (the server stamps the offset of the oldest row actually returned),
  // and a bad cursor is INVALID_PARAMS by design — falling back to the tail
  // would hand us the newest block labelled as the older one, which we would
  // splice into scrollback as history. So a rejection SURFACES and is never
  // retried bare.
  const loadOlder = useCallback(() => {
    const st = store.getState();
    const ss = st.getSession(sessionKey);
    if (ss.loadingOlder || !ss.hasMoreHistory || !ss.historyCursor) return;
    const lxCtx = parseLxSessionKey(sessionKey);
    if (!lxCtx || (lxCtx.type !== "case" && lxCtx.type !== "global")) return;
    const rpcNow = rpcRef.current;
    if (!rpcNow) return;
    const params = lxScopeParams(lxCtx);
    st.setLoadingOlder(sessionKey, true);
    rpcNow
      .lxChatHistory({ ...params, before: ss.historyCursor })
      .then((result) => {
        if (result?.readError) {
          store.getState().setLoadingOlder(sessionKey, false);
          store.getState().setOlderError(sessionKey, result.readError);
          return;
        }
        // prependHistory dedupes by (runId, role), advances the cursor/hasMore
        // to the new oldest block, and clears loadingOlder + olderError.
        store.getState().prependHistory(
          sessionKey,
          mapLxHistoryRows(result?.messages ?? [], params),
          {
            ...(result?.cursor ? { cursor: result.cursor } : {}),
            hasMore: result?.hasMore === true,
          },
        );
      })
      .catch((err: unknown) => {
        store.getState().setLoadingOlder(sessionKey, false);
        store
          .getState()
          .setOlderError(
            sessionKey,
            err instanceof Error ? err.message : "older history load failed",
          );
      });
  }, [sessionKey, store]);

  // ── P6 item 2 — tail polling ──────────────────────────────────────────────
  // ⚖ D-8 turns land in threads nobody is prompting: the concierge narrates a
  // mission outcome into the case thread by itself, up to ~65 s after the
  // terminal. The WS path delivers those finals live — this poll is the
  // catch-up for whatever the socket missed (a reconnect gap, a frame dropped
  // while the tab slept). It runs only between turns: the WS path OWNS live
  // streaming, and merging a poll snapshot mid-run would race finalizeRun.
  // Best-effort by design — a failed tick changes nothing and never touches
  // the readError state (that is the INITIAL read's third state, not this
  // poll's to overwrite).
  useEffect(() => {
    const lxCtx = parseLxSessionKey(sessionKey);
    if (!lxCtx || (lxCtx.type !== "case" && lxCtx.type !== "global")) return;
    const params = lxScopeParams(lxCtx);
    const tick = () => {
      // A hidden tab throttles timers anyway; skipping keeps the read budget
      // where a reader actually is. Focus regain gets the next interval tick.
      if (document.visibilityState === "hidden") return;
      if (useGatewayStore.getState().connectionState !== "connected") return;
      const rpcNow = rpcRef.current;
      if (!rpcNow) return;
      const ss = store.getState().getSession(sessionKey);
      if (!ss.historyLoaded) return;
      // ⚖ The poll RUNS during an active run — but only RECONCILES, never
      // merges blindly (fixes ui-tester I-1 + I-2, run ui-20260903-2241).
      //
      // This used to `return` whenever `activeRunId` was set, to keep a poll
      // snapshot from racing `finalizeRun`. That reasoning is right about
      // merging and wrong about polling, and the combination deadlocked: the
      // ONLY things that clear `activeRunId` are wire events, so when the
      // `final` never arrives the run stays "active" forever — and this poll,
      // whose entire job is catching what the socket missed, is disabled by
      // exactly the state that proves the socket missed something.
      //
      // Measured cost (2026-09-03, TRAB-1): the turn ended at 22:55:55Z with
      // every message persisted, and 5.6 h later the tab still showed
      // "Revisando… 337m 40s" with Send disabled and typed text dropped. A
      // reload fixed it instantly — because a reload does what this poll was
      // forbidden from doing.
      //
      // ⚠ NOT a timeout. `RUN_STALL_REVEAL_MS` (10 min, operator-directed
      // 2026-08-14) answers "this has been silent a suspicious while" and is
      // untouched. This asks a different question with a hard answer: does the
      // SERVER already hold the finalized row for this run? If it does, the
      // turn is over as a matter of fact, not of elapsed time.
      const activeRunId = ss.activeRunId;
      rpcNow
        .lxChatHistory({ ...params, limit: TAIL_POLL_LIMIT })
        .then((result) => {
          if (!result || result.readError) return;
          const rows = mapLxHistoryRows(result.messages ?? [], params);
          if (!rows.length) return;
          if (activeRunId) {
            // Positive evidence only: a PERSISTED assistant row for the run we
            // still believe is streaming. Anything weaker (an older row, a user
            // row sharing the runId) proves nothing and must not release the
            // lock.
            const endedServerSide = rows.some(
              (m) => m.role === "assistant" && m.runId === activeRunId,
            );
            // ⚠ …AND the run must be quiet. A COMPACTION SPLIT shares one runId
            // across several agent runs, so a persisted row can belong to an
            // earlier segment of a turn that is still streaming. Releasing on
            // the row alone would end a live turn from the outside — the
            // "stuck promptbox traded for a LYING one" that `run-stall`'s
            // ruling 1 forbids, and the lawyer loses the answer. Deltas reset
            // this clock, so a healthy turn can never satisfy it.
            const silentFor =
              lastEventAtRef.current === null ? null : Date.now() - lastEventAtRef.current;
            const quiet = silentFor !== null && silentFor >= RUN_RECONCILE_MIN_SILENCE_MS;
            // ⚠ Bound worth knowing: this reads only the last TAIL_POLL_LIMIT
            // rows, so a run whose row has already been pushed out of that
            // window is not reconcilable here — the stall-release affordance
            // remains the backstop for that case.
            if (!endedServerSide || !quiet) return; // in flight, or not yet provably over
            log.warn("chat.run.reconciled_from_history", {
              session_key: sessionKey,
              run_id: activeRunId,
              silent_for_ms: silentFor,
              threshold_ms: RUN_RECONCILE_MIN_SILENCE_MS,
            });
            store.getState().reconcileEndedRun(sessionKey, activeRunId);
          }
          // appendTailHistory drops every row already present by
          // (runId, role) — a poll racing the WS final is a no-op.
          const backfilled = store.getState().appendTailHistory(sessionKey, rows);
          // ⚖ I-2's INSTRUMENT. Anything this poll had to add is a row the live
          // WS path never rendered — the "produced but not rendered" branch.
          // I-2 has TWO causes sharing one signature and only one is explained,
          // so the honest deliverable is a signal that says WHICH happened
          // rather than a speculative fix: rows appearing here means they were
          // produced and the live path missed them; a drop with NOTHING to
          // backfill means they were never produced at all (not this surface).
          // Deliberately prod-visible — the DEV-only `__chatDropped` tally
          // cannot see a defect that only shows up on a lawyer's machine.
          if (backfilled > 0) {
            log.warn("chat.history.backfilled_rows_live_path_missed", {
              session_key: sessionKey,
              rows: backfilled,
              during_active_run: activeRunId !== null,
            });
          }
        })
        .catch(() => {
          // Transient poll failure — the next tick tries again; nothing to
          // surface for a mechanism whose whole job is redundancy.
        });
    };
    const id = window.setInterval(tick, TAIL_POLL_MS);
    return () => window.clearInterval(id);
  }, [sessionKey, store]);

  // ── P6 item 3 — active-session registration for the unread tracker ────────
  // The app-level `useChatUnreadTracker` needs to know which session's chat
  // surface is mounted so its finals never count as unread; and opening a
  // case is what CLEARS that case's unread state. Layouts render one chat
  // surface at a time, so last-mount-wins is sufficient; the unmount guard
  // only resets the key when it is still ours (a successor mount already
  // overwrote it otherwise).
  useEffect(() => {
    const unread = useChatUnreadStore.getState();
    unread.setActiveSessionKey(sessionKey);
    const ctx = parseLxSessionKey(sessionKey);
    if (ctx?.type === "case" && ctx.caseId) unread.clearCase(ctx.caseId);
    return () => {
      const st = useChatUnreadStore.getState();
      if (st.activeSessionKey === sessionKey) st.setActiveSessionKey(null);
    };
  }, [sessionKey]);

  return {
    messages: session.messages,
    // Live plan-tool cards render ABOVE the streaming text (Claude Code
    // style) and survive into the finalized message via finalizeRun's merge.
    streamingBlocks:
      session.liveToolBlocks.length > 0
        ? [...session.liveToolBlocks, ...session.streamingBlocks]
        : session.streamingBlocks,
    isStreaming: session.activeRunId !== null,
    activity: session.activity,
    isLoading: session.isLoading,
    historyLoaded: session.historyLoaded,
    error: session.error,
    send,
    abort,
    retryLast,
    /** ⚖ WEDGE row — true once the run has been silent past the reveal
     *  threshold. Consumers OFFER `releaseStalledRun`; nothing acts on its own. */
    isStalled: stalled,
    releaseStalledRun,
    historyReadError: session.historyReadError,
    // Composite on purpose: `hasMore` without a cursor (a contradictory or
    // pre-paging server page) would arm a sentinel that can never load.
    hasMoreHistory: session.hasMoreHistory && session.historyCursor !== null,
    loadingOlder: session.loadingOlder,
    olderError: session.olderError,
    loadOlder,
  };
}

// `redactSystemTrailEntry` moved to `lib/chat/tool-activity.ts` (2026-08-15,
// A-11 render half) so the spread-keeps-unknown-fields behavior is pinnable by
// a unit test without importing this hook's full module graph.
